import axios from '@/plugins/axiosInstance'
import { socialMedia } from '@/networking/urlManager'
import { responseHandlerService } from '@/plugins/responseHandlerService'

/**
 * Fetch social media posts for a given asset
 * @param {string} assetId - Asset ID
 * @param {Object} params - Query parameters for pagination and filtering
 * @param {number} params.page - Page number
 * @param {number} params.limit - Number of items per page
 * @param {string} params.sortBy - Sort field
 * @param {string} params.sortOrder - Sort direction ('asc' or 'desc')
 * @returns {Promise<Object>} Posts data with pagination info
 */
export const getPosts = async (assetId, params = {}, retryCount = 0) => {
  try {
    const response = await axios.get(socialMedia.getPosts, {
      params: {
        assetId,
        ...params,
      },
      timeout: 15000 // 15 seconds timeout
    })
    return response.data.data
  } catch (error) {
    // Check if we should retry
    if (retryCount < 2 && (error.code === 'ECONNABORTED' || !error.response)) {
      // Wait 1 second before retrying
      await new Promise(resolve => setTimeout(resolve, 1000))
      return getPosts(assetId, params, retryCount + 1)
    }
    
    console.error('Error fetching social media posts:', error)
    responseHandlerService.handleError('Failed to fetch posts. Please try again.')
    return { detail: [], totalCount: 0, pageCount: 0 }
  }
}

/**
 * Fetch a single social media post detail
 * @param {string} postId - Post ID
 * @param {string} assetId - Asset ID
 * @returns {Promise<Object>} Post detail data
 */
export const getPostDetail = async (postId, assetId) => {
  // Ensure postId is a valid primitive value
  if (!postId) {
    console.error('getPostDetail received null or undefined postId')
    return null
  }
  
  if (typeof postId === 'object') {
    if (postId?.__v_isRef) {
      // Handle Vue ref
      postId = postId.value
      
      if (!postId) {
        console.error('getPostDetail: Ref value is null or undefined')
        return null
      }
    } else {
      postId = postId?.id || postId?.postId
    }
  }
  
  if (!postId) {
    console.error('getPostDetail: postId is still invalid after processing')
    return null
  }
  
  // Convert to string for URL parameters
  postId = String(postId)
  try {
    const response = await axios.get(socialMedia.getPost, {
      params: {
        postId,
        assetId,
      },
    })
    
    return response.data.data
  } catch (error) {
    console.error('Error fetching post detail:', error)
    responseHandlerService.handleError('Failed to fetch post details. Please try again.')
    return null
  }
}
